import { useFontRender } from "@/components/FontRender";
import FormatedText from "@/components/FormatedText";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { useAPI } from "@/lib/api";
import { IMicrositeComponent, MComponentContactProps } from "@/types/microsite";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import dynamic from "next/dynamic";
import { Fragment, Suspense, useEffect, useState } from "react";
import slugify from "slugify";
import {
  DummyIcon,
  LinkComponentStyle,
  StyledIcon,
  StyledLink,
  StyledText,
  TextOuter,
} from "./LinkComponents";
import { useProtected } from "./ProtectedModal";

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

const ContactComponent = {
  kind: "CONTACT",
  Render: ({
    microsite,
    isBuilder,
    id,
    data: {
      content,
      icon,
      textFont,
      buttonEffect,
      buttonColor,
      textPosition,
      needPassword: _needPassword,
      passwordMessage,
      phone: _phone,
      email: _email,
    },
  }) => {
    const api = useAPI();
    const withIcon = !!icon;
    const [needPassword, setNeedPassword] = useState(_needPassword);
    const [{ phone, email }, setData] = useState({
      phone: _phone,
      email: _email,
    });
    const { focusTo } = useComponent();

    const { fontStyle, RenderFontStyle } = useFontRender({ textFont });

    const buttonType = microsite?.style?.buttonType || "elevate";
    const hasButtonShadow = !!microsite?.style?.buttonShadowColor;
    const buttonBorderColor = microsite?.style?.buttonBorderColor;

    const protectedProp = useProtected({
      micrositeId: microsite?.linkId,
      componentId: id,
      message: passwordMessage,
      isBuilder,
      onSuccess: (data) => {
        setData(data);
        track();
        setNeedPassword(false);
      },
    });

    const handleLink = (e) => {
      if (isBuilder) {
        e.preventDefault();
        focusTo(id as string);
        return;
      }

      if (needPassword) {
        e.preventDefault();
        protectedProp.onRequest();
        return;
      }

      downloadVcard();

      track();
    };

    const track = () => {
      const post = {
        component_id: id,
        action_type: "button",
      };
      api.post("/track/microsite", post).then(() => {
        //...
      });
    };

    const downloadVcard = () => {
      const data = ["BEGIN:VCARD", "VERSION:3.0", "FN:" + microsite?.title];

      if (phone) {
        data.push("TEL:" + phone);
      }

      if (email) {
        data.push("EMAIL;CHARSET=UTF-8;type=HOME,INTERNET:" + email);
      }

      data.push("END:VCARD");

      const vCardBlob = new Blob([data.join("\n")], { type: "text/vcard" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(vCardBlob);
      link.download = `${slugify(microsite?.title || "", {
        lower: true,
        trim: true,
        strict: true,
      })}.vcf`;
      link.click();
      URL.revokeObjectURL(link.href);
    };

    useEffect(() => {
      setData({ phone: _phone, email: _email });
    }, [_phone, _email]);

    if (content?.length > 0) {
      return (
        <Fragment>
          {RenderFontStyle}
          <LinkComponentStyle id={isBuilder ? `mcomp-${id}` : undefined}>
            <StyledLink
              buttonColor={buttonColor}
              buttonEffect={buttonEffect}
              buttonType={buttonType}
              hasButtonShadow={hasButtonShadow}
              buttonBorderColor={buttonBorderColor}
              onClick={handleLink}
              isButton
            >
              {withIcon ? (
                <StyledIcon buttonType={buttonType} className="">
                  <Suspense>
                    <RenderIcon icon={icon} />
                  </Suspense>
                </StyledIcon>
              ) : (
                <DummyIcon textPosition={textPosition} left />
              )}
              <TextOuter className="">
                <StyledText
                  style={fontStyle}
                  position={textPosition || "left"}
                  className=""
                >
                  <FormatedText value={content} singleLine />
                </StyledText>
              </TextOuter>
              {needPassword ? (
                <StyledIcon buttonType={buttonType} className="">
                  <LockClosedIcon className="w-5" />
                </StyledIcon>
              ) : (
                <DummyIcon textPosition={textPosition} right />
              )}
            </StyledLink>
          </LinkComponentStyle>
        </Fragment>
      );
    }

    return <Fragment />;
  },
} as IMicrositeComponent<MComponentContactProps>;

export default ContactComponent;
